/**
 * The answer key of a slitherlink is the loop. Kept apart from the drawing component so the
 * eagerly loaded module file never pulls the author's form into the player bundle.
 */
import { edgesOf } from "./progress";
import type { Edges, SlitherlinkDefinition, SlitherlinkSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no edges at all, so the form can tell
 * "nothing typed" from a deliberately blank key. `loopOf` pads it to the grid.
 */
export function emptySolution(_definition: SlitherlinkDefinition): SlitherlinkSolution {
  return { h: [], v: [] };
}

/** Whether a stored value has the shape of edges at all, whatever its size. */
export function isEdges(value: unknown): value is SlitherlinkSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const { h, v } = value as { h?: unknown; v?: unknown };
  const lines = (x: unknown) =>
    Array.isArray(x) && x.every((line) => typeof line === "string" && /^[#.]*$/.test(line));
  return lines(h) && lines(v);
}

/** Nothing queued: not edges, or the empty value the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isEdges(value) || (value.h.length === 0 && value.v.length === 0);
}

/** Reads whatever is stored, however wrong, as a loop of the definition's size. */
export function loopOf(value: unknown, definition: SlitherlinkDefinition): Edges {
  return edgesOf(value, definition.rows, definition.cols);
}
